import React, { useEffect, useState, useMemo, useCallback } from 'react';
import { Link } from 'react-router-dom';
import {
  CalendarCheck,
  Plus,
  Clock,
  User,
  ShieldCheck,
  CheckCircle2,
  AlertCircle,
  RefreshCw,
  Search,
  Filter,
  Trash2,
  Edit3,
  X,
  ChevronDown,
  ChevronUp,
  AlertTriangle,
  Award,
} from 'lucide-react';
import { portalApi } from '../../../../lib/portalApi';
import { useAuth } from '../../../../context/AuthContext';
import ConfirmDeleteModal from '../../shared/ConfirmDeleteModal';
import AdminMentorControls from '../components/AdminMentorControls';

function getKolkataToday() {
  try {
    return new Intl.DateTimeFormat('en-CA', {
      timeZone: 'Asia/Kolkata',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    }).format(new Date());
  } catch {
    return new Date().toISOString().slice(0, 10);
  }
}

function formatDate(dateStr) {
  if (!dateStr) return '';
  try {
    const d = new Date(dateStr);
    return d.toLocaleDateString('en-IN', {
      timeZone: 'Asia/Kolkata',
      day: 'numeric',
      month: 'short',
      year: 'numeric',
    });
  } catch {
    return dateStr;
  }
}

function formatDeadlineTime(dateStr) {
  if (!dateStr) return '';
  try {
    const d = new Date(dateStr);
    return d.toLocaleTimeString('en-IN', {
      timeZone: 'Asia/Kolkata',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return '';
  }
}

const STATUS_CONFIG = {
  todo: {
    label: 'To Do',
    badge: 'bg-slate-500/20 text-slate-300 border-slate-500/30',
    color: 'text-slate-300',
  },
  in_progress: {
    label: 'In Progress',
    badge: 'bg-sky-500/20 text-sky-300 border-sky-500/30',
    color: 'text-sky-300',
  },
  completed: {
    label: 'Completed',
    badge: 'bg-emerald-500/20 text-emerald-300 border-emerald-500/30',
    color: 'text-emerald-300',
  },
  blocked: {
    label: 'Blocked',
    badge: 'bg-rose-500/20 text-rose-300 border-rose-500/30',
    color: 'text-rose-300',
  },
};

export default function AdminDailyTasksPage() {
  const { user } = useAuth();

  const isManager = useMemo(() => {
    if (!user) return false;
    const roles = user.roles || [];
    if (roles.includes('superadmin')) return true;
    const features = user.adminFeatures || [];
    return features.includes('manage_daily_tasks') || features.includes('user_management');
  }, [user]);

  const canAccessInvites = useMemo(() => {
    if (!user) return false;
    return (user.roles || []).includes('superadmin');
  }, [user]);

  // Data states
  const [tasks, setTasks] = useState([]);
  const [subadmins, setSubadmins] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [actionMessage, setActionMessage] = useState('');

  // Filters
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [subadminFilter, setSubadminFilter] = useState('');
  const [dateFilterMode, setDateFilterMode] = useState('all'); // 'all' | 'today' | 'custom'
  const [customDate, setCustomDate] = useState('');
  const [overdueOnly, setOverdueOnly] = useState(false);

  // Modals & form states
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [createForm, setCreateForm] = useState({
    title: '',
    description: '',
    assignedTo: '',
    taskDate: getKolkataToday(),
    dueTime: '',
  });
  const [createBusy, setCreateBusy] = useState(false);
  const [createError, setCreateError] = useState('');

  const [editTask, setEditTask] = useState(null);
  const [editForm, setEditForm] = useState({
    title: '',
    description: '',
    assignedTo: '',
    taskDate: '',
    dueTime: '',
    status: 'todo',
    statusReason: '',
    reassignmentReason: '',
  });
  const [editBusy, setEditBusy] = useState(false);
  const [editError, setEditError] = useState('');

  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleteBusy, setDeleteBusy] = useState(false);
  const [deleteError, setDeleteError] = useState('');

  const [statusModalTask, setStatusModalTask] = useState(null);
  const [statusForm, setStatusForm] = useState({ status: 'in_progress', statusReason: '' });
  const [statusBusy, setStatusBusy] = useState(false);
  const [statusError, setStatusError] = useState('');
  const [mentorControlsOpen, setMentorControlsOpen] = useState(false);

  // Expanded task instruction cards
  const [expandedCards, setExpandedCards] = useState({});

  const toggleExpand = (id) => {
    setExpandedCards((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  const loadSubadmins = useCallback(async () => {
    if (!isManager) return;
    try {
      const list = await portalApi.listEligibleSubadmins();
      setSubadmins(Array.isArray(list) ? list : []);
    } catch {
      setSubadmins([]);
    }
  }, [isManager]);

  const loadTasks = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      let params = {};
      if (statusFilter) params.status = statusFilter;
      if (overdueOnly) params.overdue = true;
      if (dateFilterMode === 'today') {
        params.date = getKolkataToday();
      } else if (dateFilterMode === 'custom' && customDate) {
        params.date = customDate;
      }

      if (isManager) {
        if (subadminFilter) params.subadminId = subadminFilter;
        const res = await portalApi.listManagedDailyTasks(params);
        setTasks(Array.isArray(res) ? res : []);
      } else {
        const res = await portalApi.listMyDailyTasks(params);
        setTasks(Array.isArray(res) ? res : []);
      }
    } catch (err) {
      setError(err.message || 'Failed to load daily tasks');
      setTasks([]);
    } finally {
      setLoading(false);
    }
  }, [isManager, statusFilter, overdueOnly, dateFilterMode, customDate, subadminFilter]);

  useEffect(() => {
    loadTasks();
  }, [loadTasks]);

  useEffect(() => {
    if (isManager) {
      loadSubadmins();
    }
  }, [isManager, loadSubadmins]);

  // Client-side text search filter
  const filteredTasks = useMemo(() => {
    if (!search.trim()) return tasks;
    const q = search.toLowerCase();
    return tasks.filter((t) => {
      const matchTitle = t.title?.toLowerCase().includes(q);
      const matchDesc = t.description?.toLowerCase().includes(q);
      const matchAssignee = t.assignedToName?.toLowerCase().includes(q);
      const matchAssigner = t.assignedByName?.toLowerCase().includes(q);
      return matchTitle || matchDesc || matchAssignee || matchAssigner;
    });
  }, [tasks, search]);

  // Handle task creation
  const handleCreateSubmit = async (e) => {
    e.preventDefault();
    if (!createForm.title.trim()) {
      setCreateError('Task title is required');
      return;
    }
    if (!createForm.assignedTo) {
      setCreateError('Please select an eligible subadmin');
      return;
    }
    setCreateBusy(true);
    setCreateError('');
    try {
      let deadline = null;
      if (createForm.taskDate && createForm.dueTime) {
        deadline = new Date(`${createForm.taskDate}T${createForm.dueTime}:00+05:30`);
      } else if (createForm.taskDate) {
        deadline = new Date(`${createForm.taskDate}T23:59:59.999+05:30`);
      }

      await portalApi.createDailyTask({
        title: createForm.title.trim(),
        description: createForm.description.trim(),
        assignedTo: createForm.assignedTo,
        taskDate: createForm.taskDate || getKolkataToday(),
        deadline,
      });

      setCreateModalOpen(false);
      setCreateForm({
        title: '',
        description: '',
        assignedTo: '',
        taskDate: getKolkataToday(),
        dueTime: '',
      });
      setActionMessage('Task assigned successfully');
      setTimeout(() => setActionMessage(''), 3500);
      await loadTasks();
    } catch (err) {
      setCreateError(err.message || 'Failed to create task');
    } finally {
      setCreateBusy(false);
    }
  };

  // Handle opening edit/reassign modal
  const openEditModal = (task) => {
    if (isManager) loadSubadmins();
    setEditTask(task);
    let dueTime = '';
    if (task.deadline) {
      try {
        const d = new Date(task.deadline);
        const hours = new Intl.DateTimeFormat('en-IN', {
          timeZone: 'Asia/Kolkata',
          hour: '2-digit',
          hour12: false,
        }).format(d).padStart(2, '0');
        const minutes = new Intl.DateTimeFormat('en-IN', {
          timeZone: 'Asia/Kolkata',
          minute: '2-digit',
        }).format(d).padStart(2, '0');
        dueTime = `${hours}:${minutes}`;
      } catch {
        dueTime = '';
      }
    }
    setEditForm({
      title: task.title || '',
      description: task.description || '',
      assignedTo: task.assignedTo?._id || task.assignedTo || '',
      taskDate: task.taskDateStr || getKolkataToday(),
      dueTime,
      status: task.status || 'todo',
      statusReason: task.statusReason || '',
      reassignmentReason: '',
    });
    setEditError('');
  };

  const handleEditSubmit = async (e) => {
    e.preventDefault();
    if (!editTask) return;
    setEditBusy(true);
    setEditError('');
    try {
      let deadline = null;
      if (editForm.taskDate && editForm.dueTime) {
        deadline = new Date(`${editForm.taskDate}T${editForm.dueTime}:00+05:30`);
      } else if (editForm.taskDate) {
        deadline = new Date(`${editForm.taskDate}T23:59:59.999+05:30`);
      }

      await portalApi.updateDailyTask(editTask._id || editTask.id, {
        title: editForm.title.trim(),
        description: editForm.description.trim(),
        assignedTo: editForm.assignedTo,
        taskDate: editForm.taskDate,
        deadline,
        status: editForm.status,
        statusReason: editForm.statusReason,
        reassignmentReason: editForm.reassignmentReason,
      });

      setEditTask(null);
      setActionMessage('Task updated successfully');
      setTimeout(() => setActionMessage(''), 3500);
      await loadTasks();
    } catch (err) {
      setEditError(err.message || 'Failed to update task');
    } finally {
      setEditBusy(false);
    }
  };

  // Handle subadmin progress update
  const openStatusModal = (task) => {
    setStatusModalTask(task);
    setStatusForm({
      status: task.status || 'todo',
      statusReason: task.statusReason || '',
    });
    setStatusError('');
  };

  const handleStatusSubmit = async (e) => {
    e.preventDefault();
    if (!statusModalTask) return;
    setStatusBusy(true);
    setStatusError('');
    try {
      await portalApi.updateDailyTaskProgress(statusModalTask._id || statusModalTask.id, {
        status: statusForm.status,
        statusReason: statusForm.statusReason,
      });
      setStatusModalTask(null);
      setActionMessage('Progress updated successfully');
      setTimeout(() => setActionMessage(''), 3500);
      await loadTasks();
    } catch (err) {
      setStatusError(err.message || 'Failed to update progress');
    } finally {
      setStatusBusy(false);
    }
  };

  // Quick inline status change for subadmin
  const handleQuickStatusChange = async (task, newStatus) => {
    try {
      await portalApi.updateDailyTaskProgress(task._id || task.id, { status: newStatus });
      await loadTasks();
    } catch (err) {
      setError(err.message || 'Could not update status');
    }
  };

  // Handle task deletion
  const confirmDelete = async () => {
    if (!deleteTarget) return;
    setDeleteBusy(true);
    setDeleteError('');
    try {
      await portalApi.deleteDailyTask(deleteTarget._id || deleteTarget.id);
      setDeleteTarget(null);
      setActionMessage('Task deleted successfully');
      setTimeout(() => setActionMessage(''), 3500);
      await loadTasks();
    } catch (err) {
      setDeleteError(err.message || 'Failed to delete task');
    } finally {
      setDeleteBusy(false);
    }
  };

  return (
    <div className="space-y-6 max-w-7xl mx-auto">
      {/* Top Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 pb-2 border-b border-white/10">
        <div>
          <h1 className="text-2xl font-bold text-white flex items-center gap-2.5">
            <CalendarCheck className="w-6 h-6 text-[#d42b92]" />
            {isManager ? 'Daily Tasks' : 'My Daily Tasks'}
          </h1>
          <p className="text-sm text-white/50 mt-1">
            {isManager
              ? 'Assign, monitor, and manage daily staff tasks for eligible subadmins.'
              : 'Review your assigned daily tasks, check instructions and deadlines, and update progress.'}
          </p>
        </div>

        <div className="flex items-center gap-3">
          <button
            type="button"
            onClick={() => {
              loadTasks();
              if (isManager) loadSubadmins();
            }}
            disabled={loading}
            className="p-2.5 rounded-xl border border-white/10 bg-white/[0.02] text-white/70 hover:text-white hover:border-white/20 transition-all text-sm flex items-center gap-2"
            title="Refresh tasks"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin text-[#ff8ec5]' : ''}`} />
            <span className="hidden sm:inline">Refresh</span>
          </button>

          {isManager && (
            <>
              <button
                type="button"
                onClick={() => setMentorControlsOpen(true)}
                className="px-3.5 py-2.5 rounded-xl border border-[#d42b92]/50 bg-[#321129]/60 hover:bg-[#321129] text-[#ff8ec5] hover:text-white font-medium text-xs sm:text-sm transition-all flex items-center gap-2"
                title="Assign Task, KRA or KPI to Mentors"
              >
                <Award className="w-4 h-4 text-[#ff8ec5]" />
                <span>Mentor Tasks</span>
              </button>
              <button
                type="button"
                onClick={() => {
                  setCreateModalOpen(true);
                  setCreateError('');
                  loadSubadmins();
                }}
                className="px-4 py-2.5 rounded-xl bg-[#d42b92] hover:bg-[#b02279] text-white font-medium text-sm transition-all shadow-lg shadow-[#d42b92]/20 flex items-center gap-2"
              >
                <Plus className="w-4 h-4" />
                Assign Staff Task
              </button>
            </>
          )}
        </div>
      </div>

      {/* Action / Success Banner */}
      {actionMessage && (
        <div className="p-3.5 rounded-xl bg-emerald-500/15 border border-emerald-500/30 text-emerald-300 text-sm flex items-center gap-2">
          <CheckCircle2 className="w-4 h-4 shrink-0" />
          <span>{actionMessage}</span>
        </div>
      )}

      {/* Global error banner */}
      {error && (
        <div className="p-3.5 rounded-xl bg-rose-500/15 border border-rose-500/30 text-rose-300 text-sm flex items-center gap-2">
          <AlertCircle className="w-4 h-4 shrink-0" />
          <span>{error}</span>
        </div>
      )}

      {/* Filter and Control Bar */}
      <div className="p-4 rounded-2xl bg-white/[0.02] border border-white/10 space-y-3">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
          {/* Search box */}
          <div className="relative">
            <Search className="w-4 h-4 absolute left-3 top-3 text-white/40" />
            <input
              type="text"
              placeholder="Search by title, instructions, staff..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full pl-9 pr-4 py-2 bg-black/40 border border-white/10 rounded-xl text-white text-sm focus:outline-none focus:border-[#d42b92]"
            />
          </div>

          {/* Status Filter */}
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="w-full px-3 py-2 bg-black/40 border border-white/10 rounded-xl text-white text-sm focus:outline-none focus:border-[#d42b92]"
          >
            <option value="">All Statuses</option>
            <option value="todo">To Do</option>
            <option value="in_progress">In Progress</option>
            <option value="completed">Completed</option>
            <option value="blocked">Blocked</option>
          </select>

          {/* Subadmin Filter (Manager only) */}
          {isManager ? (
            <select
              value={subadminFilter}
              onChange={(e) => setSubadminFilter(e.target.value)}
              className="w-full px-3 py-2 bg-black/40 border border-white/10 rounded-xl text-white text-sm focus:outline-none focus:border-[#d42b92]"
            >
              <option value="">All Assigned Subadmins</option>
              {subadmins.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.fullName || s.name || s.email}
                </option>
              ))}
            </select>
          ) : (
            <div className="px-3 py-2 rounded-xl bg-white/[0.03] border border-white/5 text-xs text-white/60 flex items-center gap-2">
              <User className="w-4 h-4 text-[#ff8ec5]" />
              <span>Assigned to: {user?.fullName || user?.name || user?.email}</span>
            </div>
          )}

          {/* Date Scope Filter */}
          <div className="flex gap-1.5">
            <button
              type="button"
              onClick={() => {
                setDateFilterMode('all');
                setCustomDate('');
              }}
              className={`flex-1 px-3 py-2 rounded-xl text-xs font-medium transition-all ${
                dateFilterMode === 'all'
                  ? 'bg-[#d42b92]/20 text-[#ff8ec5] border border-[#d42b92]/40'
                  : 'bg-black/30 text-white/60 border border-white/10 hover:text-white'
              }`}
            >
              All Dates
            </button>
            <button
              type="button"
              onClick={() => {
                setDateFilterMode('today');
                setCustomDate('');
              }}
              className={`flex-1 px-3 py-2 rounded-xl text-xs font-medium transition-all ${
                dateFilterMode === 'today'
                  ? 'bg-[#d42b92]/20 text-[#ff8ec5] border border-[#d42b92]/40'
                  : 'bg-black/30 text-white/60 border border-white/10 hover:text-white'
              }`}
            >
              Today
            </button>
            <button
              type="button"
              onClick={() => {
                setDateFilterMode('custom');
                if (!customDate) setCustomDate(getKolkataToday());
              }}
              className={`flex-1 px-3 py-2 rounded-xl text-xs font-medium transition-all ${
                dateFilterMode === 'custom'
                  ? 'bg-[#d42b92]/20 text-[#ff8ec5] border border-[#d42b92]/40'
                  : 'bg-black/30 text-white/60 border border-white/10 hover:text-white'
              }`}
            >
              Custom
            </button>
          </div>
        </div>

        {/* Extended Date Row & Overdue Toggle */}
        <div className="flex flex-wrap items-center justify-between gap-3 pt-2 border-t border-white/5 text-xs">
          <div className="flex items-center gap-3">
            {dateFilterMode === 'custom' && (
              <div className="flex items-center gap-2">
                <span className="text-white/50">Pick Date:</span>
                <input
                  type="date"
                  value={customDate}
                  onChange={(e) => setCustomDate(e.target.value)}
                  className="bg-black/50 border border-white/15 rounded-lg px-2.5 py-1 text-white text-xs"
                />
              </div>
            )}
            <span className="text-white/40">
              Showing {filteredTasks.length} task{filteredTasks.length === 1 ? '' : 's'}
            </span>
          </div>

          <label className="flex items-center gap-2 cursor-pointer text-white/70 hover:text-white">
            <input
              type="checkbox"
              checked={overdueOnly}
              onChange={(e) => setOverdueOnly(e.target.checked)}
              className="rounded border-white/20 text-[#d42b92] focus:ring-0 bg-black/40"
            />
            <span className="text-xs">Show Overdue Only</span>
          </label>
        </div>
      </div>

      {/* Task List Content */}
      {loading ? (
        <div className="py-20 text-center text-white/40 flex flex-col items-center justify-center gap-3">
          <RefreshCw className="w-8 h-8 animate-spin text-[#d42b92]" />
          <span>Loading tasks…</span>
        </div>
      ) : filteredTasks.length === 0 ? (
        <div className="py-16 text-center rounded-2xl border border-white/10 bg-white/[0.01] p-8 space-y-3">
          <div className="w-12 h-12 rounded-2xl bg-white/5 border border-white/10 mx-auto flex items-center justify-center text-white/40">
            <CalendarCheck className="w-6 h-6" />
          </div>
          <div className="text-white font-medium text-base">No daily tasks found</div>
          <p className="text-xs text-white/50 max-w-sm mx-auto">
            {isManager
              ? 'No daily tasks match the selected filters. Use "Assign Task" to create one.'
              : 'You do not have any tasks assigned for the selected criteria. Keep up the great work!'}
          </p>
          {isManager && (
            <button
              type="button"
              onClick={() => setCreateModalOpen(true)}
              className="mt-2 inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-[#d42b92] text-white text-xs font-medium"
            >
              <Plus className="w-3.5 h-3.5" /> Assign First Task
            </button>
          )}
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredTasks.map((t) => {
            const isCompleted = t.status === 'completed';
            const isOverdue =
              !isCompleted && t.deadline && new Date(t.deadline).getTime() < Date.now();
            const statusCfg = STATUS_CONFIG[t.status] || STATUS_CONFIG.todo;
            const isExpanded = !!expandedCards[t._id];

            return (
              <div
                key={t._id}
                className={`rounded-2xl border bg-white/[0.02] p-5 flex flex-col justify-between transition-all hover:border-white/20 ${
                  isOverdue
                    ? 'border-rose-500/40 shadow-sm shadow-rose-950/30'
                    : 'border-white/10'
                }`}
              >
                {/* Header & Badges */}
                <div className="space-y-3">
                  <div className="flex items-start justify-between gap-2">
                    <span
                      className={`text-xs px-2.5 py-0.5 rounded-full border font-medium ${statusCfg.badge}`}
                    >
                      {statusCfg.label}
                    </span>

                    <div className="flex items-center gap-1.5">
                      {isOverdue && (
                        <span className="text-[11px] px-2 py-0.5 rounded-full border border-rose-500/40 bg-rose-500/20 text-rose-300 font-semibold flex items-center gap-1">
                          <AlertTriangle className="w-3 h-3" /> Overdue
                        </span>
                      )}

                      {/* Manager Controls */}
                      {isManager && (
                        <div className="flex items-center gap-1 ml-1">
                          <button
                            type="button"
                            onClick={() => openEditModal(t)}
                            title="Edit / Reassign Task"
                            className="p-1.5 rounded-lg text-white/50 hover:text-white hover:bg-white/10 transition-colors"
                          >
                            <Edit3 className="w-3.5 h-3.5" />
                          </button>
                          <button
                            type="button"
                            onClick={() => {
                              setDeleteTarget(t);
                              setDeleteError('');
                            }}
                            title="Delete Task"
                            className="p-1.5 rounded-lg text-rose-400/80 hover:text-rose-300 hover:bg-rose-500/10 transition-colors"
                          >
                            <Trash2 className="w-3.5 h-3.5" />
                          </button>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Task Title */}
                  <h3 className="text-base font-semibold text-white leading-snug break-words">
                    {t.title}
                  </h3>

                  {/* Instructions preview / expand */}
                  {t.description && (
                    <div className="space-y-1">
                      <p
                        className={`text-xs text-white/70 whitespace-pre-wrap leading-relaxed ${
                          isExpanded ? '' : 'line-clamp-2'
                        }`}
                      >
                        {t.description}
                      </p>
                      {t.description.length > 90 && (
                        <button
                          type="button"
                          onClick={() => toggleExpand(t._id)}
                          className="text-[11px] text-[#ff8ec5] hover:underline flex items-center gap-1"
                        >
                          {isExpanded ? (
                            <>
                              Show less <ChevronUp className="w-3 h-3" />
                            </>
                          ) : (
                            <>
                              Read more <ChevronDown className="w-3 h-3" />
                            </>
                          )}
                        </button>
                      )}
                    </div>
                  )}

                  {/* Status Reason / Note if any */}
                  {t.statusReason && (
                    <div className="p-2.5 rounded-xl bg-black/40 border border-white/5 text-xs text-white/60 space-y-0.5">
                      <span className="text-[10px] uppercase font-semibold text-white/40 tracking-wider">
                        Progress Note:
                      </span>
                      <p className="italic text-white/80">{t.statusReason}</p>
                    </div>
                  )}
                </div>

                {/* Footer Metadata & Attribution */}
                <div className="mt-5 pt-4 border-t border-white/10 space-y-2.5 text-xs">
                  {/* Recipient attribution */}
                  <div className="flex items-center justify-between text-white/70">
                    <span className="text-white/40">Assigned to:</span>
                    <span className="font-medium text-white flex items-center gap-1.5">
                      <User className="w-3.5 h-3.5 text-sky-400" />
                      {t.assignedToName || t.assignedTo?.fullName || t.assignedToEmail || 'Subadmin'}
                    </span>
                  </div>

                  {/* CRITICAL ATTRIBUTION REQUIREMENT: Assigned by */}
                  <div className="flex items-center justify-between text-white/70">
                    <span className="text-white/40">Assigned by:</span>
                    <span className="font-medium text-[#ff8ec5] flex items-center gap-1.5">
                      <ShieldCheck className="w-3.5 h-3.5 text-[#d42b92]" />
                      {t.assignedByName || t.assignedBy?.fullName || 'Administrator'}
                    </span>
                  </div>

                  {/* Date & Deadline */}
                  <div className="flex items-center justify-between text-white/50 text-[11px]">
                    <span className="flex items-center gap-1">
                      <CalendarCheck className="w-3 h-3 text-white/40" />
                      Date: {formatDate(t.taskDate)}
                    </span>
                    {t.deadline && (
                      <span
                        className={`flex items-center gap-1 font-medium ${
                          isOverdue ? 'text-rose-400' : 'text-white/60'
                        }`}
                      >
                        <Clock className="w-3 h-3" />
                        Due: {formatDate(t.deadline)} {formatDeadlineTime(t.deadline)}
                      </span>
                    )}
                  </div>

                  {/* Subadmin status action bar (recipient self-service) */}
                  {!isManager ? (
                    <div className="pt-2 border-t border-white/5 flex items-center gap-1.5">
                      <select
                        value={t.status}
                        onChange={(e) => handleQuickStatusChange(t, e.target.value)}
                        className="flex-1 bg-black/60 border border-white/15 rounded-lg px-2.5 py-1.5 text-xs text-white focus:outline-none focus:border-[#d42b92]"
                      >
                        <option value="todo">Mark To Do</option>
                        <option value="in_progress">Mark In Progress</option>
                        <option value="completed">Mark Completed</option>
                        <option value="blocked">Mark Blocked</option>
                      </select>
                      <button
                        type="button"
                        onClick={() => openStatusModal(t)}
                        title="Add progress note"
                        className="px-2.5 py-1.5 rounded-lg border border-white/10 bg-white/5 hover:bg-white/10 text-white/70 hover:text-white text-xs"
                      >
                        Note
                      </button>
                    </div>
                  ) : (
                    /* Manager status view / quick review */
                    <div className="pt-2 border-t border-white/5 flex items-center justify-between text-[11px] text-white/40">
                      <span>Original Creator: {t.createdByName || 'Admin'}</span>
                      {t.reassignmentHistory?.length > 0 && (
                        <span className="text-[#ff8ec5]">
                          Reassigned ({t.reassignmentHistory.length})
                        </span>
                      )}
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* CREATE TASK MODAL (Manager Only) */}
      {createModalOpen && (
        <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4 backdrop-blur-sm">
          <div className="bg-[#0e0e16] border border-white/15 rounded-2xl max-w-lg w-full p-6 space-y-4 shadow-2xl relative">
            <div className="flex items-center justify-between pb-3 border-b border-white/10">
              <h2 className="text-lg font-semibold text-white flex items-center gap-2">
                <Plus className="w-5 h-5 text-[#d42b92]" /> Assign Daily Task
              </h2>
              <button
                type="button"
                onClick={() => setCreateModalOpen(false)}
                className="p-1 rounded-lg text-white/40 hover:text-white hover:bg-white/10"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {createError && (
              <div className="p-3 rounded-xl bg-rose-500/15 border border-rose-500/30 text-rose-300 text-xs">
                {createError}
              </div>
            )}

            <form onSubmit={handleCreateSubmit} className="space-y-4">
              <div>
                <label className="block text-xs font-medium text-white/70 mb-1">
                  Task Title <span className="text-[#d42b92]">*</span>
                </label>
                <input
                  required
                  type="text"
                  placeholder="e.g. Verify KYC documents for Cohort 4 candidates"
                  value={createForm.title}
                  onChange={(e) => setCreateForm({ ...createForm, title: e.target.value })}
                  className="w-full bg-black/50 border border-white/15 rounded-xl px-3.5 py-2.5 text-white text-sm focus:outline-none focus:border-[#d42b92]"
                />
              </div>

              <div>
                <label className="block text-xs font-medium text-white/70 mb-1">
                  Instructions / Description
                </label>
                <textarea
                  rows={3}
                  placeholder="Provide detailed instructions, links, or expectations for the subadmin..."
                  value={createForm.description}
                  onChange={(e) => setCreateForm({ ...createForm, description: e.target.value })}
                  className="w-full bg-black/50 border border-white/15 rounded-xl px-3.5 py-2 text-white text-sm focus:outline-none focus:border-[#d42b92]"
                />
              </div>

              <div>
                <label className="block text-xs font-medium text-white/70 mb-1">
                  Assign To Subadmin <span className="text-[#d42b92]">*</span>
                </label>
                <select
                  required
                  value={createForm.assignedTo}
                  onChange={(e) => setCreateForm({ ...createForm, assignedTo: e.target.value })}
                  className="w-full bg-black/50 border border-white/15 rounded-xl px-3.5 py-2.5 text-white text-sm focus:outline-none focus:border-[#d42b92]"
                >
                  <option value="">Select an eligible subadmin…</option>
                  {subadmins.map((s) => (
                    <option key={s.id} value={s.id}>
                      {s.fullName || s.name} ({s.email})
                    </option>
                  ))}
                </select>
                {subadmins.length === 0 && (
                  <p className="text-[11px] text-amber-400/80 mt-1">
                    No active staff subadmins found.{' '}
                    {canAccessInvites ? (
                      <Link to="/admin/invites" className="underline text-[#ff8ec5] hover:text-white font-medium">
                        Click here to invite staff via Invites &rarr;
                      </Link>
                    ) : (
                      'Please contact a Superadmin to invite staff subadmins.'
                    )}
                  </p>
                )}
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium text-white/70 mb-1">Task Date</label>
                  <input
                    type="date"
                    value={createForm.taskDate}
                    onChange={(e) => setCreateForm({ ...createForm, taskDate: e.target.value })}
                    className="w-full bg-black/50 border border-white/15 rounded-xl px-3 py-2 text-white text-sm focus:outline-none focus:border-[#d42b92]"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-white/70 mb-1">
                    Due Time (Optional)
                  </label>
                  <input
                    type="time"
                    value={createForm.dueTime}
                    onChange={(e) => setCreateForm({ ...createForm, dueTime: e.target.value })}
                    className="w-full bg-black/50 border border-white/15 rounded-xl px-3 py-2 text-white text-sm focus:outline-none focus:border-[#d42b92]"
                  />
                </div>
              </div>

              <div className="flex items-center justify-end gap-3 pt-3 border-t border-white/10">
                <button
                  type="button"
                  onClick={() => setCreateModalOpen(false)}
                  className="px-4 py-2 rounded-xl border border-white/15 text-white/70 hover:text-white text-sm"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={createBusy || !createForm.title.trim() || !createForm.assignedTo}
                  className="px-5 py-2 rounded-xl bg-[#d42b92] hover:bg-[#b02279] text-white text-sm font-medium disabled:opacity-40 transition-all flex items-center gap-2"
                >
                  {createBusy ? 'Assigning…' : 'Assign Task'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* EDIT / REASSIGN MODAL (Manager Only) */}
      {editTask && (
        <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4 backdrop-blur-sm">
          <div className="bg-[#0e0e16] border border-white/15 rounded-2xl max-w-lg w-full p-6 space-y-4 shadow-2xl relative">
            <div className="flex items-center justify-between pb-3 border-b border-white/10">
              <h2 className="text-lg font-semibold text-white flex items-center gap-2">
                <Edit3 className="w-5 h-5 text-[#ff8ec5]" /> Edit / Reassign Task
              </h2>
              <button
                type="button"
                onClick={() => setEditTask(null)}
                className="p-1 rounded-lg text-white/40 hover:text-white hover:bg-white/10"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {editError && (
              <div className="p-3 rounded-xl bg-rose-500/15 border border-rose-500/30 text-rose-300 text-xs">
                {editError}
              </div>
            )}

            <form onSubmit={handleEditSubmit} className="space-y-4">
              <div>
                <label className="block text-xs font-medium text-white/70 mb-1">Task Title</label>
                <input
                  required
                  type="text"
                  value={editForm.title}
                  onChange={(e) => setEditForm({ ...editForm, title: e.target.value })}
                  className="w-full bg-black/50 border border-white/15 rounded-xl px-3.5 py-2.5 text-white text-sm focus:outline-none focus:border-[#d42b92]"
                />
              </div>

              <div>
                <label className="block text-xs font-medium text-white/70 mb-1">
                  Instructions / Description
                </label>
                <textarea
                  rows={3}
                  value={editForm.description}
                  onChange={(e) => setEditForm({ ...editForm, description: e.target.value })}
                  className="w-full bg-black/50 border border-white/15 rounded-xl px-3.5 py-2 text-white text-sm focus:outline-none focus:border-[#d42b92]"
                />
              </div>

              <div>
                <label className="block text-xs font-medium text-white/70 mb-1">
                  Assigned Subadmin (Reassign if needed)
                </label>
                <select
                  required
                  value={editForm.assignedTo}
                  onChange={(e) => setEditForm({ ...editForm, assignedTo: e.target.value })}
                  className="w-full bg-black/50 border border-white/15 rounded-xl px-3.5 py-2.5 text-white text-sm focus:outline-none focus:border-[#d42b92]"
                >
                  {subadmins.map((s) => (
                    <option key={s.id} value={s.id}>
                      {s.fullName || s.name} ({s.email})
                    </option>
                  ))}
                </select>
                {editForm.assignedTo !== (editTask.assignedTo?._id || editTask.assignedTo) && (
                  <p className="text-[11px] text-[#ff8ec5] mt-1">
                    Reassigning will transfer responsibility to the new subadmin. Previous subadmin will lose personal access.
                  </p>
                )}
              </div>

              {editForm.assignedTo !== (editTask.assignedTo?._id || editTask.assignedTo) && (
                <div>
                  <label className="block text-xs font-medium text-white/70 mb-1">
                    Reason for Reassignment (Optional)
                  </label>
                  <input
                    type="text"
                    placeholder="e.g. Workload balancing, shift handover"
                    value={editForm.reassignmentReason}
                    onChange={(e) =>
                      setEditForm({ ...editForm, reassignmentReason: e.target.value })
                    }
                    className="w-full bg-black/50 border border-white/15 rounded-xl px-3 py-2 text-white text-sm focus:outline-none focus:border-[#d42b92]"
                  />
                </div>
              )}

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium text-white/70 mb-1">Task Date</label>
                  <input
                    type="date"
                    value={editForm.taskDate}
                    onChange={(e) => setEditForm({ ...editForm, taskDate: e.target.value })}
                    className="w-full bg-black/50 border border-white/15 rounded-xl px-3 py-2 text-white text-sm focus:outline-none focus:border-[#d42b92]"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-white/70 mb-1">
                    Due Time (Optional)
                  </label>
                  <input
                    type="time"
                    value={editForm.dueTime}
                    onChange={(e) => setEditForm({ ...editForm, dueTime: e.target.value })}
                    className="w-full bg-black/50 border border-white/15 rounded-xl px-3 py-2 text-white text-sm focus:outline-none focus:border-[#d42b92]"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium text-white/70 mb-1">Status</label>
                  <select
                    value={editForm.status}
                    onChange={(e) => setEditForm({ ...editForm, status: e.target.value })}
                    className="w-full bg-black/50 border border-white/15 rounded-xl px-3 py-2 text-white text-sm focus:outline-none focus:border-[#d42b92]"
                  >
                    <option value="todo">To Do</option>
                    <option value="in_progress">In Progress</option>
                    <option value="completed">Completed</option>
                    <option value="blocked">Blocked</option>
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-medium text-white/70 mb-1">
                    Progress Note
                  </label>
                  <input
                    type="text"
                    placeholder="Status explanation"
                    value={editForm.statusReason}
                    onChange={(e) => setEditForm({ ...editForm, statusReason: e.target.value })}
                    className="w-full bg-black/50 border border-white/15 rounded-xl px-3 py-2 text-white text-sm focus:outline-none focus:border-[#d42b92]"
                  />
                </div>
              </div>

              <div className="flex items-center justify-end gap-3 pt-3 border-t border-white/10">
                <button
                  type="button"
                  onClick={() => setEditTask(null)}
                  className="px-4 py-2 rounded-xl border border-white/15 text-white/70 hover:text-white text-sm"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={editBusy}
                  className="px-5 py-2 rounded-xl bg-[#d42b92] hover:bg-[#b02279] text-white text-sm font-medium disabled:opacity-40 transition-all"
                >
                  {editBusy ? 'Saving…' : 'Save Changes'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* SUBADMIN PROGRESS / NOTE MODAL */}
      {statusModalTask && (
        <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4 backdrop-blur-sm">
          <div className="bg-[#0e0e16] border border-white/15 rounded-2xl max-w-md w-full p-6 space-y-4 shadow-2xl relative">
            <div className="flex items-center justify-between pb-3 border-b border-white/10">
              <h2 className="text-lg font-semibold text-white flex items-center gap-2">
                <CheckCircle2 className="w-5 h-5 text-emerald-400" /> Update Task Progress
              </h2>
              <button
                type="button"
                onClick={() => setStatusModalTask(null)}
                className="p-1 rounded-lg text-white/40 hover:text-white hover:bg-white/10"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {statusError && (
              <div className="p-3 rounded-xl bg-rose-500/15 border border-rose-500/30 text-rose-300 text-xs">
                {statusError}
              </div>
            )}

            <form onSubmit={handleStatusSubmit} className="space-y-4">
              <div>
                <label className="block text-xs font-medium text-white/70 mb-1">Current Status</label>
                <select
                  value={statusForm.status}
                  onChange={(e) => setStatusForm({ ...statusForm, status: e.target.value })}
                  className="w-full bg-black/50 border border-white/15 rounded-xl px-3.5 py-2.5 text-white text-sm focus:outline-none focus:border-[#d42b92]"
                >
                  <option value="todo">To Do</option>
                  <option value="in_progress">In Progress</option>
                  <option value="completed">Completed</option>
                  <option value="blocked">Blocked</option>
                </select>
              </div>

              <div>
                <label className="block text-xs font-medium text-white/70 mb-1">
                  Progress Note / Blocker Reason (Optional)
                </label>
                <textarea
                  rows={3}
                  placeholder="e.g. Completed verification for 12 founders. 2 awaiting phone verification."
                  value={statusForm.statusReason}
                  onChange={(e) =>
                    setStatusForm({ ...statusForm, statusReason: e.target.value })
                  }
                  className="w-full bg-black/50 border border-white/15 rounded-xl px-3.5 py-2 text-white text-sm focus:outline-none focus:border-[#d42b92]"
                />
              </div>

              <div className="flex items-center justify-end gap-3 pt-3 border-t border-white/10">
                <button
                  type="button"
                  onClick={() => setStatusModalTask(null)}
                  className="px-4 py-2 rounded-xl border border-white/15 text-white/70 hover:text-white text-sm"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={statusBusy}
                  className="px-5 py-2 rounded-xl bg-[#d42b92] hover:bg-[#b02279] text-white text-sm font-medium disabled:opacity-40 transition-all"
                >
                  {statusBusy ? 'Saving…' : 'Save Progress'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* CONFIRM DELETE MODAL (Manager Only) */}
      <ConfirmDeleteModal
        open={!!deleteTarget}
        onClose={() => {
          if (!deleteBusy) {
            setDeleteTarget(null);
            setDeleteError('');
          }
        }}
        onConfirm={confirmDelete}
        title="Delete Daily Task"
        entityName={deleteTarget?.title}
        entityDetail={`Assigned to: ${deleteTarget?.assignedToName || 'Subadmin'} · Due: ${formatDate(
          deleteTarget?.taskDate
        )}`}
        description="Are you sure you want to permanently delete this daily task? This action cannot be undone."
        warning="This internal staff task will be removed from all staff lists."
        busy={deleteBusy}
        error={deleteError}
        confirmLabel="Delete Task"
      />

      {mentorControlsOpen && <AdminMentorControls onClose={() => setMentorControlsOpen(false)} />}
    </div>
  );
}
